import { FIRST_LOAD_STATE_WAIT_MS } from 'projects/service/src/app/shared/utils/grid-persistence';
import { ActionIconDef, GridConfig, GridRow } from '@inavista/components/grid';

// Attachments tab (Service Order, ISC and the other popups that host app-attachments-tab) on @inavista/components.
// Same columns, labels, default visibility, widths and edit rule as the Syncfusion grid it replaces. Rows are the
// tab's own `attachments` array (LocalRowsGridApi, no endpoint); a Comments edit is saved at once, as before.

const BLACK = '#000000de';
const GREEN = '#5cb85c';
const INACTIVE = '#cccccc';
const MAIL_ICON = '<rect x="3" y="5" width="18" height="14" rx="2" /><polyline points="3 7 12 13 21 7" />';
const DOWNLOAD_ICON = '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />';
const TRASH_ICON = '<polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />';

export const ATTACHMENTS_PERSISTENCE_KEY = 'rims.service.attachments-tab';
/** The grid's row key: the attachment's index in the tab's array (new uploads have attachmentId 0). */
export const ATTACHMENT_ROW_KEY = 'rowKey';

export const ATTACHMENT_COLUMNS: [string, string][] = [
  ['serialNumber', 'int'],
  ['fileName', 'nvarchar'],
  ['fileType', 'nvarchar'],
  ['fileId', 'nvarchar'],
  ['lastUpdatedDate', 'date'],
  ['fileSize', 'bigint'],
  ['comment', 'nvarchar'],
];

export interface AttachmentsGridContext {
  /** Why Comments can't be edited (the old beginEdit cancel), or null. */
  editLockReason: () => string | null;
  /** Why this row's Email icon is disabled (the old rowDataBound rule), or null. */
  emailLockReason: (row: GridRow) => string | null;
  /** Why this row's Delete icon is disabled (the old rowDataBound rule), or null. */
  deleteLockReason: (row: GridRow) => string | null;
  fileSize: (size: unknown) => string;
  canExport: boolean;
}

export function buildAttachmentsActionIcons(ctx: AttachmentsGridContext): ActionIconDef[] {
  return [
    { key: 'email', label: 'Email Attachment', icon: MAIL_ICON,
      colorFn: row => (ctx.emailLockReason(row) ? INACTIVE : BLACK),
      disabledFn: row => !!ctx.emailLockReason(row),
      labelFn: row => ctx.emailLockReason(row) ?? 'Email Attachment' },
    { key: 'download', label: 'Download Attachment', icon: DOWNLOAD_ICON, colorFn: () => GREEN },
    { key: 'delete', label: 'Delete Attachment', icon: TRASH_ICON,
      colorFn: row => (ctx.deleteLockReason(row) ? INACTIVE : BLACK),
      disabledFn: row => !!ctx.deleteLockReason(row),
      labelFn: row => ctx.deleteLockReason(row) ?? 'Delete Attachment' },
  ];
}

export function buildAttachmentsGridConfig(ctx: AttachmentsGridContext): GridConfig {
  return {
    apiBaseUrl: 'page://attachments',
    clientSide: true,
    keyColumn: ATTACHMENT_ROW_KEY,
    // 30, as the other Service Order popup grids.
    rowHeight: 30,
    defaultColumnOrder: ATTACHMENT_COLUMNS.map(([c]) => c),
    // File Type and File Id were hidden at start (still in the column chooser).
    startHiddenColumns: ['fileType', 'fileId'],
    columnLabels: {
      serialNumber: 'Sr. No', fileName: 'File name', fileType: 'File Type', fileId: 'File Id',
      lastUpdatedDate: 'Last Updated', fileSize: 'Size', comment: 'Comments',
    },
    columnAlign: Object.fromEntries(ATTACHMENT_COLUMNS.map(([c]) => [c, {
      // Every column left, as the old grid (Martin: same look as before).
      header: { horizontal: 'left' as const },
    }])),
    // Sr. No 120 and Last Updated 160 as before; the other columns shared the rest, so they stretch.
    columnWidths: { serialNumber: 120, fileName: 220, fileType: 150, fileId: 220, lastUpdatedDate: 160, fileSize: 150, comment: 220 },
    columnSizing: { serialNumber: { noStretch: true }, lastUpdatedDate: { noStretch: true } },
    stretchColumns: true,
    // Actions 140, as before.
    actionsColumnMinWidth: 140,
    columnTypes: { lastUpdatedDate: 'date', fileSize: 'number' },
    columnDateFormats: { lastUpdatedDate: 'd MMM, yyyy' },
    // The old size template: "12 KB" / "3 MB". Sort and filter stay on the bytes, as before.
    columnFormatters: { fileSize: value => ctx.fileSize(value) },
    excelFilterColumns: ['fileType'],
    // Excel export, same as before: only with the export claim.
    enableExport: ctx.canExport,
    enableSavedViews: false,
    // The old toolbar: Columns, Search, Excel export, Clear filters. Reset layout is kept: it undoes the saved layout.
    enableScrollButtons: false,
    enableFitColumns: false,
    enableResetLayout: true,
    enableRefresh: false,
    enableFooter: false,
    editMode: 'inline',
    editSaveMode: 'row',
    // A double click opens the row, as before.
    editTrigger: 'doubleClick',
    editableColumns: ['comment'],
    readOnlyCellFn: () => !!ctx.editLockReason(),
    deleteMode: 'host',
    rowDeletable: () => false,
    emptyStateMessage: 'No attachments.',
    // Right-click a cell filters by its value (Martin, 2026-10-29: every grid that filters).
    enableContextMenuFilter: true,
    persistenceKey: ATTACHMENTS_PERSISTENCE_KEY,
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
  };
}
